import { collectionSpec, type Manifest } from "./manifest";
import { applyOp, checkRecord, fieldChanges, type Json, type Op, type Rec, type RecordChange } from "./records";
import { allowed, type Partition, type Writer } from "./rules";

export type { Op } from "./records";

/**
 * What the server does with every write to a partition (docs/views.md#2-data and docs/views.md#9-refused-writes), whether a device's operation, an
 * agent's, a seed's, an integration's, or a runner's: apply it to the record as it is, check the collection's scope and
 * rule, stamp who and when on what the rule allows, number a new record, and keep an entry of history for each. What
 * the rule refuses changes nothing and is reported. An operation that can't apply at all, such as a patch to a record
 * that isn't there, fails the batch, so a partition's operations apply together or not at all.
 */
export interface HistoryEntry {
  collection: string;
  id: string;
  at: string;
  by: string;
  via?: string;
  /** The view's action that made the change. */
  action?: string;
  created?: true;
  removed?: true;
  fields: Record<string, [Json, Json]>;
}
export interface Staged {
  /** The changes to save and send to everyone who reads the partition, the server's stamps in place. */
  accepted: RecordChange[];
  refused: RecordChange[];
  history: HistoryEntry[];
}
export interface StageContext {
  partition: Partition;
  manifest: Pick<Manifest, "collections">;
  writer: Writer;
  at: string;
  /** The next number in a collection, for a new record. */
  number(collection: string): number;
  /** A record as the partition holds it now, or null. */
  read(collection: string, id: string): Rec | null;
}

export function stageOps(context: StageContext, ops: Op[]): Staged {
  if (!Array.isArray(ops) || !ops.length || ops.length > 100) throw new Error("Send 1 to 100 operations.");
  const { writer, partition, at } = context;
  const by = writer.kind === "person" ? writer.id : `${writer.kind}:${writer.id}`;
  // Records this batch has changed, so a later operation sees an earlier one's work.
  const changed = new Map<string, Rec | null>();
  const current = (collection: string, id: string) => {
    const key = `${collection}/${id}`;
    return changed.has(key) ? (changed.get(key) ?? null) : context.read(collection, id);
  };
  const accepted: RecordChange[] = [],
    refused: RecordChange[] = [],
    history: HistoryEntry[] = [];
  for (const op of ops) {
    if (!op || typeof op !== "object" || !["insert", "update", "put", "remove"].includes(op.op))
      throw new Error("Unknown operation.");
    const before = current(op.collection, op.id);
    const { kind, after } = applyOp(before, op);
    if (after) checkRecord(after);
    const spec = collectionSpec(context.manifest, op.collection);
    // A collection's records live where its scope says: a chat's in that chat's partition, and so on.
    const placed = spec.scope === partition.scope;
    if (!placed || !allowed(spec, partition, { kind, id: op.id, before }, writer)) {
      refused.push({ collection: op.collection, id: op.id, kind, before, after: before, fields: {} });
      continue;
    }
    const stamped: Rec | null = after && {
      ...after,
      n: kind === "insert" ? context.number(op.collection) : (before?.n ?? null),
      createdAt: kind === "insert" ? at : (before?.createdAt ?? null),
      createdBy: kind === "insert" ? by : (before?.createdBy ?? null),
      updatedAt: at,
      updatedBy: by,
    };
    changed.set(`${op.collection}/${op.id}`, stamped);
    const fields = fieldChanges(before, stamped);
    accepted.push({ collection: op.collection, id: op.id, kind, before, after: stamped, fields });
    // An update that changed no field has nothing for history to say; the record still takes the writer's stamp.
    if (kind !== "update" || Object.keys(fields).length)
      history.push({
        collection: op.collection,
        id: op.id,
        at,
        by,
        ...(writer.via ? { via: writer.via } : {}),
        ...(writer.action ? { action: writer.action } : {}),
        ...(kind === "insert" ? { created: true as const } : {}),
        ...(kind === "remove" ? { removed: true as const } : {}),
        fields,
      });
  }
  return { accepted, refused, history };
}
